import { Row, Badge, Col, Button } from "reactstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy } from "@fortawesome/free-solid-svg-icons";
import ButtonSendActivationMail from "../Button/ButtonSendActivationMail";
import ButtonCreatePasswordResetLink from "../Button/ButtonCreatePasswordResetLink";
import ButtonMoveUser from "../Button/ButtonMoveUser";
import ButtonRestoreUser from "../Button/ButtonRestoreUser";
import UserTypeIcon from "../Badges/UserTypeIcon";
import { copyToClipboard } from "../../Utilities/clipboardHelper";

export default function FoundUserInfo(props) {
  const intl = useIntl();

  // Lärarens härledda handledarkonto nås via lärarens konto, så där finns ingen länk att skapa.
  const canCreateResetLink =
    props.foundUser.Activated &&
    !props.foundUser.IsDeleted &&
    props.foundUser.Type !== "Analytics" &&
    !props.foundUser.Email?.includes("_apl_spv");

  // En raderad elev eller lärare flyttas och återställs i samma anrop.
  const canMove =
    props.foundUser.Type === "Student" || props.foundUser.Type === "Teacher";

  if (props.foundUser.UserFound !== false) {
    return (
      <Row>
        <Col xs="12">
          <div className="card card-body mt-3" style={{ width: "70%" }}>
            <div className="align-items-baseline d-flex">
              <p className="lead float-start ms-3">
                <FormattedMessage id="Id" />
              </p>
              <p className="float-start ms-2">{props.foundUser.Id}</p>
            </div>

            <div className="align-items-baseline d-flex">
              <p className="lead float-start ms-3">
                <FormattedMessage id="Email" />:
              </p>
              <p className="float-start ms-2">{props.foundUser.Email}</p>
              <Button
                type="button"
                color="primary"
                outline
                size="sm"
                className="ms-2"
                title={intl.formatMessage({ id: "Copy" })}
                onClick={() => copyToClipboard(props.foundUser.Email, intl)}
              >
                <FontAwesomeIcon icon={faCopy} style={{ opacity: 0.75 }} />
              </Button>
            </div>

            <div className="align-items-baseline d-flex">
              <p className="lead float-start ms-3">
                <FormattedMessage id="Created" />:
              </p>
              <p className="float-start ms-2">{props.foundUser.Created}</p>
            </div>

            <div className="align-items-baseline d-flex">
              <p className="lead float-start ms-3">
                <FormattedMessage id="LastSeen" />:
              </p>
              <p className="float-start ms-2">{props.foundUser.LastSeen}</p>
            </div>

            <div className="align-items-baseline d-flex">
              <p className="lead float-start ms-3">
                <FormattedMessage id="Activated" />:
              </p>
              <p className="float-start ms-2">
                {props.foundUser.Activated ? (
                  <FormattedMessage id="true" />
                ) : (
                  <Badge color="secondary">
                    <FormattedMessage id="false" />
                  </Badge>
                )}
              </p>
              {!props.foundUser.Activated && !props.foundUser.IsDeleted && (
                <div className="ms-3 d-flex">
                  <ButtonSendActivationMail
                    user={{ email: props.foundUser.Email }}
                    useDialog={true}
                    printButtonText={true}
                    leftAlign={true}
                  />
                </div>
              )}
              {canCreateResetLink && (
                <div className="ms-3 d-flex">
                  <ButtonCreatePasswordResetLink
                    email={props.foundUser.Email}
                  />
                </div>
              )}
            </div>

            <div className="align-items-baseline d-flex">
              <p className="lead float-start ms-3">
                <FormattedMessage id="IsDeleted" />:
              </p>
              <p className="float-start ms-2">
                {props.foundUser.IsDeleted ? (
                  <Badge color="secondary">
                    {props.foundUser.DeletedAt || <FormattedMessage id="true" />}
                  </Badge>
                ) : (
                  <FormattedMessage id="false" />
                )}
              </p>
              {props.foundUser.IsDeleted && props.foundUser.CanBeRestored && (
                <div className="ms-3 d-flex">
                  <ButtonRestoreUser
                    email={props.foundUser.Email}
                    onRestored={props.onUserChanged}
                  />
                </div>
              )}
            </div>

            <div className="align-items-baseline d-flex">
              <p className="lead float-start ms-3">
                <FormattedMessage id="Name" />:
              </p>
              <p
                className="float-start ms-2"
                style={{
                  textDecorationLine: props.foundUser.IsDeleted
                    ? "line-through"
                    : "none",
                }}
              >
                {props.foundUser.Name}
              </p>
            </div>

            <div className="align-items-baseline d-flex">
              <p className="lead float-start ms-3">
                <FormattedMessage id="Type" />:
              </p>
              <p className="float-start ms-2">
                {props.foundUser.Type}
                <UserTypeIcon
                  userType={props.foundUser.Type}
                  className="ms-2"
                />
              </p>
            </div>

            {props.foundUser.Type === "Teacher" && (
              <div className="align-items-baseline d-flex">
                <p className="lead float-start ms-3">
                  <FormattedMessage id="FullTeacherAccess" />:{" "}
                </p>
                <p className="float-start ms-2">
                  {props.foundUser.HasFullTeacherAccess ? (
                    <FormattedMessage id="true" />
                  ) : (
                    <FormattedMessage id="false" />
                  )}
                </p>
              </div>
            )}

            {props.foundUser.Type === "Teacher" && (
              <div className="align-items-baseline d-flex">
                <p className="lead float-start ms-3">
                  <FormattedMessage id="IsPurchaser" />:{" "}
                </p>
                <p className="float-start ms-2">
                  {props.foundUser.IsPurchaser ? (
                    <FormattedMessage id="true" />
                  ) : (
                    <FormattedMessage id="false" />
                  )}
                </p>
              </div>
            )}

            {props.foundUser.Type !== "Superuser" &&
              props.foundUser.Type !== "Admin" && (
                <div className="align-items-baseline d-flex">
                  <p className="lead float-start ms-3">
                    {props.foundUser.Schools.length > 1 ? (
                      <FormattedMessage id="Schools" />
                    ) : (
                      <FormattedMessage id="School" />
                    )}
                  </p>
                  {props.foundUser.Schools.map((school, index) => (
                    <span
                      className={index === 0 ? "ms-2" : ""}
                      key={`school_${index}`}
                    >
                      {(index ? ", " : "") + school.name + " ("}
                      <FormattedMessage id="FortnoxNumber" />{" "}
                      {": " + school.fortNoxNumber + ")"}
                    </span>
                  ))}
                </div>
              )}

            {props.foundUser.Type !== "Superuser" &&
              props.foundUser.Type !== "Admin" && (
                <div className="d-flex align-items-baseline">
                  <p className="lead float-start ms-3">
                    {props.foundUser.Classes.length > 1 ? (
                      <FormattedMessage id="Classes" />
                    ) : (
                      <>
                        <FormattedMessage id="Class" />:
                      </>
                    )}
                  </p>
                  <div className="d-flex flex-wrap gap-2">
                    {props.foundUser.Classes.map((cl, index) => (
                      <span
                        key={`class_${index}`}
                        color="light"
                        className="float-start ms-2"
                        title={
                          props.foundUser.ClassIsDeleted
                            ? intl.formatMessage({ id: "ClassIsDeleted" })
                            : undefined
                        }
                        style={{
                          textDecorationLine: props.foundUser.ClassIsDeleted
                            ? "line-through"
                            : "none",
                        }}
                      >
                        {cl}
                      </span>
                    ))}
                  </div>
                  {canMove && (
                    <div className="ms-3 d-flex">
                      <ButtonMoveUser
                        user={props.foundUser}
                        onMoved={props.onUserChanged}
                      />
                    </div>
                  )}
                </div>
              )}

            {props.foundUser.Type === "Student" && (
              <div className="align-items-baseline d-flex">
                <p className="lead float-start ms-3">
                  <FormattedMessage id="LicenseOnSchool" />:
                </p>
                <p className="float-start ms-2">
                  {props.foundUser.LicenceOnSchool}
                </p>
              </div>
            )}

            {props.foundUser.Type === "Student" && (
              <div className="d-flex align-items-baseline">
                <p className="lead float-start ms-3">
                  <FormattedMessage id="AplLicense" />:
                </p>
                <div className="float-start ms-2">
                  {props.foundUser.AplLicenseValidFrom && (
                    <>
                      {props.foundUser.AplLicenseValidFrom} –{" "}
                      {props.foundUser.AplLicenseValidTo}{" "}
                      <Badge
                        color={
                          props.foundUser.AplLicenseStatus === "Valid"
                            ? "primary"
                            : "secondary"
                        }
                      >
                        <FormattedMessage
                          id={`AplLicenseStatus${props.foundUser.AplLicenseStatus}`}
                        />
                      </Badge>
                    </>
                  )}
                </div>
              </div>
            )}

            <div className="d-flex align-items-baseline">
              <p className="lead float-start ms-3">
                <FormattedMessage id="AplAppInfo" />:
              </p>
              <div className="float-start ms-2">
                {props.foundUser.AplAppInfo}
              </div>
            </div>

            {props.foundUser.Type === "Student" && (
              <div className="d-flex align-items-baseline">
                <p className="lead float-start ms-3">
                  <FormattedMessage id="Licenses" />:
                </p>
                <div className="d-flex flex-wrap gap-2 mt-1 ms-2">
                  {props.foundUser.ProgramLicenses.map(
                    (programLicense, index) => (
                      <Badge color="light" key={`program_license_${index}`}>
                        {programLicense}
                      </Badge>
                    ),
                  )}
                </div>
              </div>
            )}

            {props.foundUser.Type === "Student" && (
              <div className="d-flex align-items-baseline">
                <p className="lead float-start ms-3">
                  <FormattedMessage id="Courses" />:
                </p>
                <div className="d-flex flex-wrap gap-2 mt-1 ms-2">
                  {props.foundUser.Licenses.map((courselicense, index) => (
                    <Badge color="light" key={`course_license_${index}`}>
                      {courselicense}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Col>
      </Row>
    );
  } else {
    return (
      <Row>
        <Col xs="12">
          <div className="card card-body mt-3" style={{ width: "50%" }}>
            <p className="lead">{props.foundUser.failMessage}</p>
          </div>
        </Col>
      </Row>
    );
  }
}
